/* eslint prefer-promise-reject-errors: 0 */
/* eslint no-nested-ternary: 0 */
import axios from 'axios';
import { Transport, ESPLoader } from 'esptool-js';
import * as routes from '@educabot/educablocks-cosmos';
import { getAgentPorts, getNewMessage, getNewProgress, getNewState, getPorts } from '../actions/bloquesAction';

const ESP_BUILD_FALLBACK = 'https://builder.staging.educabot.com/esp_build';
// const ESP_BUILD_FALLBACK = 'http://localhost:5000/esp_build';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const resetTransport = async (transport) => {
  try {
    if (transport.device?.readable || transport.device?.writable) {
      await transport.device.setSignals({
        dataTerminalReady: false,
        requestToSend: true,
      });
      await transport.device.setSignals({
        dataTerminalReady: false,
        requestToSend: false,
      });
      await transport.disconnect();
    }
  } catch (err) {
    console.log('resetTransport error:', err);
  }
};

class WebESPAgent {
  constructor(store, board, socketElectronConnection, httpElectronConnection, serialMonitorMessages, intl) {
    this.store = store;
    this.board = board;
    this.socketElectronConnection = socketElectronConnection;
    this.httpElectronConnection = httpElectronConnection;
    this.selectedPort = null;
    this.monitor = '';
    this.portList = [];
    this.serialPortList = [];
    this.intl = intl;
    this.serialMonitorMessages = serialMonitorMessages;
    this.monitorReader = null;
    this.monitorPort = null;

    if ('serial' in navigator) {
      navigator.serial.addEventListener('connect', (event) => {
        this.addPort(event.target);
      });
      navigator.serial.addEventListener('disconnect', (event) => {
        this.removePort(event.target);
      });
      this.store.dispatch(getNewState('#### PORT CONNECTED'));
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
    }

    this.findInitialPorts();
  }

  disconnectSocket = () => {}

  stopCheckingAgent = () => {}

  removePort = (port) => {
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].SerialPort === port) {
        this.serialPortList.splice(i, 1);
        this.portList.splice(i, 1);
        this.store.dispatch(getPorts(this.portList));
        this.store.dispatch(getAgentPorts(this.serialPortList));
        if (this.portList.length > 0) {
          this.store.dispatch(getNewState('#### BOARD CONNECTED'));
        } else {
          this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
        }
        break;
      }
    }
  }

  addPort = (port) => {
    let exists = false;
    this.serialPortList.forEach((p) => {
      if (p.SerialPort === port) {
        exists = true;
      }
    });
    if (!exists) {
      const portInfo = port.getInfo();
      const vendorID = portInfo.usbVendorId
        ? `0x${portInfo.usbVendorId.toString(16).padStart(4, '0')}`
        : 'unknown';
      const productID = portInfo.usbProductId
        ? `0x${portInfo.usbProductId.toString(16).padStart(4, '0')}`
        : 'unknown';
      const name = `Puerto (${vendorID}-${productID})`;

      this.portList.push(name);
      this.serialPortList.push({
        Name: name,
        SerialPort: port,
        VendorID: vendorID,
        ProductID: productID,
      });
      this.store.dispatch(getPorts(this.portList));
      this.store.dispatch(getAgentPorts(this.serialPortList));
      if (this.portList.length > 0) {
        this.store.dispatch(getNewState('#### BOARD CONNECTED'));
      } else {
        this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
      }
    }
  }

  getSerialPortByName = (name = '') => {
    for (let i = 0; i < this.serialPortList.length; i += 1) {
      if (this.serialPortList[i].Name === name) {
        return this.serialPortList[i].SerialPort;
      }
    }
    return null;
  }

  connectNewBoard = () => {
    if ('serial' in navigator) {
      navigator.serial.requestPort()
        .then((port) => {
          this.addPort(port);
        })
        .catch(() => {
          if (this.serialPortList.length === 0) {
            this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotSelected' })));
            this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
          }
        });
    }
  }

  findPorts = () => {}

  findInitialPorts = () => {
    if ('serial' in navigator) {
      navigator.serial.getPorts()
        .then((ports) => {
          ports.forEach((port) => this.addPort(port));
        })
        .catch((error) => {
          this.store.dispatch(getNewMessage(error.toString()));
        });
    }
  }

  getEspBuildUri = () => {
    if (process.env.IS_ELECTRON) {
      return `${this.httpElectronConnection}/esp_build`;
    }
    if (routes.espBuildUri) {
      return routes.espBuildUri;
    }
    return ESP_BUILD_FALLBACK;
  }

  flashESP = async (serialPort, fileArray, totalSize) => {
    // Close monitor if it holds a reader lock on this port
    if (this.monitorPort === serialPort && this.monitorReader) {
      await this.closeMonitor();
    }

    // Ensure port is fully closed before esptool opens it
    try {
      await serialPort.close();
    } catch (e) {
      // Port may already be closed — that's fine
    }

    // Give the OS time to release the port
    await sleep(500);

    this.store.dispatch(getNewState('#### UPLOAD TO AGENT'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 75));

    // Retry connecting — CH340 on Windows sometimes needs multiple attempts
    let transport;
    let esploader;
    const MAX_RETRIES = 3;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      transport = new Transport(serialPort);
      esploader = new ESPLoader({
        transport,
        baudrate: 115200,
        romBaudrate: 115200,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        await esploader.main_fn();
        try {
          // eslint-disable-next-line no-await-in-loop
          await esploader.flash_id();
        } catch (flashIdErr) {
          console.log('flash_id non-fatal error (continuing):', flashIdErr.message);
        }
        break; // success
      } catch (err) {
        console.log(`ESP init attempt ${attempt}/${MAX_RETRIES} error:`, err.message);
        if (attempt === MAX_RETRIES) {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(
            `Error al inicializar ESP: ${err.message || err}. Verificá que ningún otro programa tenga el puerto abierto.`,
          ));
          // eslint-disable-next-line no-await-in-loop
          await resetTransport(transport);
          return;
        }
        // Clean up before retrying
        // eslint-disable-next-line no-await-in-loop
        await resetTransport(transport);
        // eslint-disable-next-line no-await-in-loop
        await sleep(1500);
      }
    }

    // Chip validation
    const chipName = esploader.chip.CHIP_NAME.toLowerCase().replace('-', '');
    const boardFamily = this.board.compilerBoard.split(':')[0];
    if (!chipName.startsWith(boardFamily)) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(
        `Chip detectado (${esploader.chip.CHIP_NAME}) no coincide con firmware compilado para ${this.board.compilerBoard}`,
      ));
      await resetTransport(transport);
      return;
    }

    try {
      await esploader.erase_flash();
    } catch (err) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(
        `Error al borrar flash: ${err.message?.includes('The device has been lost') ? 'Se perdió la conexión' : (err.message || err)}`,
      ));
      await resetTransport(transport);
      return;
    }

    let totalWritten = 0;
    try {
      await esploader.write_flash({
        fileArray,
        flashSize: 'keep',
        flashMode: 'keep',
        flashFreq: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (fileIndex, written, total) => {
          const uncompressedWritten = (written / total) * fileArray[fileIndex].data.length;
          const newPct = Math.floor(
            ((totalWritten + uncompressedWritten) / totalSize) * 100,
          );
          if (written === total) {
            totalWritten += uncompressedWritten;
            return;
          }
          // Map 75-99 range for flash progress
          const mappedPct = 75 + Math.floor((newPct / 100) * 24);
          this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', mappedPct));
        },
      });
    } catch (err) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(
        `Error al escribir flash: ${err.message?.includes('The device has been lost') ? 'Se perdió la conexión' : (err.message || err)}`,
      ));
      await resetTransport(transport);
      return;
    }

    await sleep(100);
    await resetTransport(transport);
    this.store.dispatch(getNewState('#### FINISH AGENT'));
  }

  parseEspBuildResponse = (response) => {
    const parts = response.data.payload?.parts || response.data.parts;
    const fileArray = parts.map((part) => ({
      data: atob(part.data),
      address: parseInt(part.address, 16),
    }));
    const totalSize = fileArray.reduce((sum, f) => sum + f.data.length, 0);
    return { fileArray, totalSize };
  }

  uploadToPort = (code = '', board = '', port = '') => {
    const serialPort = this.getSerialPortByName(port);

    this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.codeIsUploading' })));
    this.store.dispatch(getNewState('#### UPLOAD TO PORT'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 0));

    const buildURI = this.getEspBuildUri();
    const buildParams = `board=${board.compilerBoard}&text=${encodeURIComponent(code)}`;

    this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));
    this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 25));

    axios.post(
      buildURI,
      buildParams,
      {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: '*/*',
        },
      },
    ).then(async (response) => {
      const parts = response.data.payload?.parts || response.data.parts;
      if ((response.data.status === 'SUCCESS' || parts) && parts) {
        this.store.dispatch(getNewState('#### FINISH EB_BUILD'));
        this.store.dispatch(getNewProgress('#### UPLOAD PROGRESS', 50));

        const { fileArray, totalSize } = this.parseEspBuildResponse(response);

        if (serialPort) {
          await this.flashESP(serialPort, fileArray, totalSize);
        } else {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotConnected' })));
        }
      } else {
        this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
        this.store.dispatch(getNewMessage(
          response.data.message
            ? (typeof response.data.message === 'string' ? response.data.message : JSON.stringify(response.data.message))
            : 'Error de compilación',
        ));
      }
    }).catch((responseError) => {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(responseError.toString()));
    });
  }

  uploadRemoteSession = (hex = '', board = '', port = '') => new Promise((resolve, reject) => {
    const serialPort = this.getSerialPortByName(port);
    this.store.dispatch(getNewState('#### UPLOAD TO PORT'));

    if (!port || port === '') {
      this.store.dispatch(getNewState('#### BOARD NOT CONNECTED'));
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardNotConnected' })));
      reject(this.intl.formatMessage({ id: 'agent.boardNotConnected' }));
      return;
    }
    this.store.dispatch(getNewState('#### BOARD CONNECTED'));
    if (!board) {
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardMustBeSelected' })));
      reject('Debes seleccionar una placa');
      return;
    }

    this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.remoteCodeIsUploading' })));
    this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));

    // hex for ESP is a JSON string with parts array
    try {
      let parts;
      if (typeof hex === 'string') {
        const parsed = JSON.parse(hex);
        parts = parsed.parts || parsed;
      } else {
        parts = hex.parts || hex;
      }

      const fileArray = parts.map((part) => ({
        data: atob(part.data),
        address: parseInt(part.address, 16),
      }));
      const totalSize = fileArray.reduce((sum, f) => sum + f.data.length, 0);

      this.flashESP(serialPort, fileArray, totalSize)
        .then(() => resolve('OK'))
        .catch((err) => {
          this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
          this.store.dispatch(getNewMessage(err.toString()));
          reject(err.toString());
        });
    } catch (err) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(`Error procesando firmware: ${err.message || err}`));
      reject(err.toString());
    }
  })

  openMonitor = async (port = '', baudios = 115200) => {
    const serialPort = this.getSerialPortByName(port);
    if (!serialPort) return;

    try {
      if (!serialPort.readable) {
        await serialPort.open({ baudRate: baudios });
      }

      this.monitorPort = serialPort;
      const reader = serialPort.readable.getReader();
      this.monitorReader = reader;
      const decoder = new TextDecoder();

      const processChunk = ({ value, done }) => {
        if (done) return;
        const message = decoder.decode(value);
        this.monitor += message;
        if (message.match(/\n/g) || this.monitor.length > 100) {
          const date = new Date();
          const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`;
          this.serialMonitorMessages.next({ time, message: this.monitor });
          this.monitor = '';
        }
        reader.read().then(processChunk).catch((err) => {
          if (err.name !== 'NetworkError') {
            console.log('Monitor read error:', err);
          }
        });
      };
      reader.read().then(processChunk).catch((err) => {
        if (err.name !== 'NetworkError') {
          console.log('Monitor read error:', err);
        }
      });
    } catch (err) {
      this.store.dispatch(getNewMessage(`Error al abrir monitor: ${err.message || err}`));
    }
  }

  closeMonitor = async (port = '') => {
    try {
      if (this.monitorReader) {
        await this.monitorReader.cancel();
        this.monitorReader.releaseLock();
        this.monitorReader = null;
      }
      if (this.monitorPort && this.monitorPort.readable) {
        await this.monitorPort.close();
      }
      this.monitorPort = null;
    } catch (err) {
      console.log('Close monitor error:', err);
    }
  }

  sendMonitor = async (port = '', command = '') => {
    const serialPort = this.getSerialPortByName(port);
    if (!serialPort || !serialPort.writable) return;

    try {
      const writer = serialPort.writable.getWriter();
      const encoder = new TextEncoder();
      await writer.write(encoder.encode(command));
      writer.releaseLock();
    } catch (err) {
      this.store.dispatch(getNewMessage(`Error al enviar comando: ${err.message || err}`));
    }
  }
}

export default WebESPAgent;
